import moment from "moment";
import momentTimezone from "moment-timezone";
import { Views } from "react-big-calendar";
import { tSales } from "../../../i18n/useAppTranslation.js";
import { checkIfPermissionAllowed, displayName } from "../../../Utils";
import { uiNotification } from "../../../services/UINotificationService.js";
import { EVENT_TYPES } from "../../../store/event/consts";
import {
  isAdditionalProviderAppointment,
  makeTitle,
  startEndDateOfWeek,
  viewDateFormat,
  showSelectedWeek,
} from "./utilities";

const RESCHEDULE_PERMISSION = "update-cancel-reschedule-appointment";
const API_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

export const CALENDAR_VIEWS = {
  verticals: "verticals",
};

export const getClinicNow = (clinicTimezone) => {
  return new Date(
    new Date().getTime() +
      (momentTimezone.tz(new Date(), clinicTimezone)._offset +
        new Date().getTimezoneOffset()) *
        60000,
  );
};

export const formatCalendarTimeRange = (
  { start, end },
  culture,
  localizer,
  timeFormat,
) => {
  const formattedStart = localizer.format(start, timeFormat, culture);
  const formattedEnd = localizer.format(end, timeFormat, culture);
  return `${formattedStart} - ${formattedEnd}`;
};

export const isWeekView = (view) => view === Views.WEEK;

export const isDayLikeView = (view) =>
  view === Views.DAY || view === CALENDAR_VIEWS.verticals;

export const getCalendarDateRange = ({ view, selectedDate }) => {
  if (isWeekView(view)) {
    return startEndDateOfWeek(selectedDate, "YYYY-MM-DD");
  }

  const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
  return { start: formattedDate, end: formattedDate };
};

export const getCalendarDateLabel = (view, date) => {
  return isWeekView(view) ? showSelectedWeek(date) : viewDateFormat(date);
};

export const getCalendarViewDateState = ({ view, date }) => {
  return {
    selectedDate: date,
    searchPickerDate: moment(date).toDate(),
    showSelectedDate: getCalendarDateLabel(view, date),
    isChangeWeek: false,
  };
};

export const getCalendarNavigationState = ({
  view,
  selectedDate,
  navigate,
}) => {
  let nextDate = moment(selectedDate);
  let isChangeWeek = false;

  if (navigate === "next") {
    nextDate = nextDate.add(1, isWeekView(view) ? "weeks" : "days");
    isChangeWeek = isWeekView(view);
  } else if (navigate === "prev") {
    nextDate = nextDate.subtract(1, isWeekView(view) ? "weeks" : "days");
    isChangeWeek = isWeekView(view);
  } else {
    nextDate = moment();
  }

  const nextDateValue = nextDate.toDate();

  return {
    selectedDate: nextDateValue,
    searchPickerDate: isWeekView(view)
      ? moment(nextDateValue).startOf("week").toDate()
      : moment(nextDateValue).toDate(),
    showSelectedDate: getCalendarDateLabel(view, nextDateValue),
    isChangeWeek,
  };
};

export const getCalendarFetchRange = ({ view, selectedDate }) => {
  return isWeekView(view)
    ? startEndDateOfWeek(selectedDate, "YYYY-MM-DD")
    : {
        start: moment(selectedDate).format("YYYY-MM-DD"),
        end: moment(selectedDate).format("YYYY-MM-DD"),
      };
};

const getEventDate = (appointment) => {
  const dateArr = appointment.appointment_date.split("-");
  const startTime = appointment.appointment_start_time.split(":");
  const endTime = appointment.appointment_end_time.split(":");

  return {
    start: new Date(
      dateArr[0],
      dateArr[1] - 1,
      dateArr[2],
      startTime[0],
      startTime[1],
      startTime[2],
    ),
    end: new Date(
      dateArr[0],
      dateArr[1] - 1,
      dateArr[2],
      endTime[0],
      endTime[1],
      endTime[2],
    ),
  };
};

const getAppointmentDisplay = (appointment, color) => {
  let title = makeTitle(appointment);
  let clientNameWithTitle;
  let eventColor = color;
  let gradient = "";

  if (appointment.type === EVENT_TYPES.appointment) {
    clientNameWithTitle = `${displayName(appointment.patient)} (${title})`;
    title = `(${title})`;
  } else if (appointment.type === EVENT_TYPES.outOfOffice) {
    if (appointment.appointment_booking) {
      clientNameWithTitle = `(${appointment.appointment_booking.appointment_notes} - Out of office)`;
    } else {
      clientNameWithTitle = "(Out of office)";
    }
    title = "(Out of office)";
    eventColor = "#000000";
  } else {
    title = "(Not Available)";
    clientNameWithTitle = "(Not Available)";
    eventColor = "#9c9c9c";
    gradient =
      "repeating-linear-gradient(to bottom, #cccccc 0px, #cccccc 69px, #9c9c9c 69px, #9c9c9c 70px)";
  }

  return {
    title,
    clientNameWithTitle,
    color: eventColor,
    gradient,
  };
};

export const mapAppointmentToCalendarEvent = (
  appointment,
  { getResourceId, getColor, hideAdditionalProviderCompletion = false } = {},
) => {
  const { start, end } = getEventDate(appointment);
  const isAdditionalProvider = isAdditionalProviderAppointment(appointment);
  const color = getColor
    ? getColor(appointment)
    : appointment.additional_provider
    ? `${appointment.color}B3`
    : appointment.color;
  const display = getAppointmentDisplay(appointment, color);
  const isFutureAppointment =
    appointment.type === EVENT_TYPES.appointment &&
    appointment.appointment_datetime > moment().format(API_DATE_TIME_FORMAT);

  return {
    id: appointment.id ? appointment.id : Math.floor(Math.random() * 1000),
    title: display.title,
    duration: appointment.duration,
    start,
    end,
    resourceId: getResourceId
      ? getResourceId(appointment)
      : parseInt(appointment.user_id),
    color: display.color,
    gradient: display.gradient,
    clientName: appointment.patient ? displayName(appointment.patient) : "",
    clientEmail:
      appointment.patient && appointment.patient.email
        ? appointment.patient.email
        : "",
    confirmed_status: isFutureAppointment
      ? Number(appointment.is_confirmed) === 0
        ? "Not Confirmed"
        : "Confirmed"
      : "",
    appointments_checkin:
      appointment.type === EVENT_TYPES.appointment &&
      appointment.appointment_type === "in_person"
        ? Number(appointment.patient_checkedin) === 1
          ? "Checked In"
          : "Not Checked In"
        : "",
    clientPhone: appointment.appointment_booking?.phone
      ? appointment.appointment_booking.phone.replace(/[- )(]/g, "")
      : "",
    clientNameWithTitle: display.clientNameWithTitle,
    checkedIn: appointment.patient_checkedin
      ? appointment.patient_checkedin
      : 0,
    appointmentType: appointment.type,
    isReschedule: !!appointment.is_reschedule,
    isAdditionalProvider,
    additionalProvider: appointment.additional_provider,
    isConsentCompleted:
      hideAdditionalProviderCompletion && isAdditionalProvider
        ? undefined
        : appointment.is_consent_completed,
    isQuestionnaireCompleted:
      hideAdditionalProviderCompletion && isAdditionalProvider
        ? undefined
        : appointment.is_questionnaire_completed,
    gfeColor: appointment.gfe_icon_color,
  };
};

export const mapAvailabilityScheduleToEvent = ({
  provider,
  schedule,
  timeFormat,
}) => {
  const dateArr = schedule.date_scheduled.split("-");
  const startTime = schedule.from_time.split(":");
  const endTime = schedule.to_time.split(":");
  const formattedFrom = moment(schedule.from_time, "HH:mm:ss").format(
    timeFormat,
  );
  const formattedTo = moment(schedule.to_time, "HH:mm:ss").format(timeFormat);

  return {
    id: provider.id,
    title: `Available for bookings from ${formattedFrom} - ${formattedTo}`,
    start: new Date(
      dateArr[0],
      dateArr[1] - 1,
      dateArr[2],
      startTime[0],
      startTime[1],
      startTime[2],
    ),
    end: new Date(
      dateArr[0],
      dateArr[1] - 1,
      dateArr[2],
      endTime[0],
      endTime[1],
      endTime[2],
    ),
    resourceId: provider.id,
    color: "#dedede",
    gradient:
      "repeating-linear-gradient(to bottom, #dedede 0px, #dedede 69px, #cccccc 69px, #cccccc 70px)",
    clientName: "",
    availabilityGrid: true,
    noClickEvents: true,
  };
};

const hasOutOfOfficeOverlap = ({
  events = [],
  start,
  ignoreAvailabilityGrid = false,
}) => {
  const format = "hh:mm:ss";
  const dropTime = moment(start, format);

  return events.some((event) => {
    if (ignoreAvailabilityGrid && event.availabilityGrid) {
      return false;
    }

    const beforeTime = moment(event.start, format);
    const afterTime = moment(event.end, format);

    return (
      (dropTime.isBetween(beforeTime, afterTime) &&
        moment(start).isSame(moment(event.end))) ||
      moment(start).isSame(moment(event.start))
    );
  });
};

const isDroppedOutsideAvailability = ({ events = [], event, start }) => {
  const format = "hh:mm:ss";
  const dropTime = moment(start, format);

  return events.some((availabilityEvent) => {
    if (
      !availabilityEvent.availabilityGrid ||
      Number(event.resourceId) !== Number(availabilityEvent.resourceId) ||
      !moment(start).isSame(availabilityEvent.start, "day")
    ) {
      return false;
    }

    const beforeTime = moment(availabilityEvent.start, format);
    const afterTime = moment(availabilityEvent.end, format);

    return (
      !dropTime.isBetween(beforeTime, afterTime) &&
      !moment(start).isSame(moment(availabilityEvent.start))
    );
  });
};

export const getMoveEventValidation = ({
  event,
  start,
  resourceId,
  events,
  checkSameStart = false,
  ignoreAvailabilityOverlap = false,
  checkOutsideAvailability = false,
}) => {
  if (event.availabilityGrid) {
    return { isValid: false, shouldReturnFalse: true };
  }

  if (event.appointmentType === EVENT_TYPES.breakTime) {
    return { isValid: false, shouldReturnFalse: true };
  }

  if (!checkIfPermissionAllowed(RESCHEDULE_PERMISSION)) {
    return { isValid: false, shouldReturnFalse: true };
  }

  if (event.checkedIn) {
    return { isValid: false, shouldReturnFalse: true };
  }

  if (event.resourceId !== resourceId) {
    return { isValid: false };
  }

  if (event.isAdditionalProvider) {
    return {
      isValid: false,
      error:
        "Appointment can only be rescheduled from the main provider's calendar",
    };
  }

  if (checkSameStart && moment(start).isSame(event.start)) {
    return { isValid: false };
  }

  if (
    event.appointmentType === EVENT_TYPES.outOfOffice &&
    !moment(start).isSame(event.start, "day")
  ) {
    return { isValid: false };
  }

  if (
    event.appointmentType === EVENT_TYPES.outOfOffice &&
    hasOutOfOfficeOverlap({
      events,
      start,
      ignoreAvailabilityGrid: ignoreAvailabilityOverlap,
    })
  ) {
    return {
      isValid: false,
      shouldReturnFalse: true,
      error: "Provider is already booked at this time!",
    };
  }

  if (
    checkOutsideAvailability &&
    event.appointmentType === EVENT_TYPES.outOfOffice &&
    isDroppedOutsideAvailability({ events, event, start })
  ) {
    return {
      isValid: false,
      shouldReturnFalse: true,
      error: "Provider is not available at this time!",
    };
  }

  const isAfterOriginalEventDate =
    moment(event.start).isAfter(new Date()) ||
    moment(event.start).isSame(new Date(), "day");
  const isAfter =
    moment(start).isAfter(new Date()) ||
    moment(start).isSame(new Date(), "day");
  const currentDayCheck = moment(event.start).diff(moment(new Date()), "days");

  return {
    isValid: isAfterOriginalEventDate && isAfter && currentDayCheck >= 0,
  };
};

export const getDroppedEventAllDayValue = ({ event, droppedOnAllDaySlot }) => {
  if (!event.allDay && droppedOnAllDaySlot) {
    return true;
  }

  if (event.allDay && !droppedOnAllDaySlot) {
    return false;
  }

  return event.allDay;
};

export const buildMoveEventState = ({
  event,
  start,
  end,
  resourceId,
  allDay,
}) => {
  const updatedEvent = { ...event, start, end, resourceId, allDay };
  const startdate = moment(start).format(API_DATE_TIME_FORMAT);

  localStorage.setItem("rescheduledTime", startdate);

  return {
    updatedEvent,
    appointment_id: event.id,
    rescheduleFormData: {
      startdate,
      appointment_id: event.id,
    },
    showRescheduleConfirmModal: true,
  };
};

export const buildRescheduleAppointmentData = (updatedEvent) => {
  return {
    appointment_id: updatedEvent.id,
    appointment_date: moment(updatedEvent.start).format("YYYY-MM-DD"),
    appointment_time: moment(updatedEvent.start).format("HH:mm:ss"),
  };
};

export const buildChargeAndRescheduleData = ({ updatedEvent, charge }) => {
  return {
    ...buildRescheduleAppointmentData(updatedEvent),
    patient_to_be_charged: charge === "true" ? 1 : 0,
  };
};

export const buildRescheduleNotificationData = ({
  appointmentId,
  notificationType,
  notificationBody,
  appointmentSubject,
  appointmentSms,
}) => {
  return {
    appointment_id: appointmentId,
    action: "rescheduled",
    notification_type: notificationType,
    notification_body: notificationBody,
    notification_subject: appointmentSubject,
    sms_body: appointmentSms,
  };
};

export const getRescheduleSuccessState = (
  data,
  { includeNotiType = false },
) => {
  const rescheduleData = data.data || {};
  const returnState = {
    appointment_email_text: rescheduleData.appointment_email_text,
    notification_body: rescheduleData.appointment_email_text,
    appointment_sms: rescheduleData.appointment_sms,
    appointment_subject: rescheduleData.appointment_subject,
    patient_name: rescheduleData.patient_name,
    success_msg: rescheduleData.success_msg,
    showAppointmentDetails: false,
    showNotifyPop: Boolean(rescheduleData.send_email_status),
    showLoader: false,
  };

  if (includeNotiType) {
    returnState.noti_type = rescheduleData.noti_type;
  }

  return returnState;
};

export const notifyRescheduleError = (res) => {
  uiNotification.error(
    tSales(`checkoutInvoice.apiError.${res.message}`, {
      defaultValue: tSales("checkoutInvoice.apiError.fallback"),
    }),
  );
};

export const getRescheduleRedirectUrl = ({ updatedEvent, clinicId, time }) => {
  let pushURL = `/appointment/edit/${updatedEvent.id}/reschedule`;

  if (time) {
    pushURL += `/${time}`;
  }

  if (clinicId) {
    pushURL += `/${clinicId}`;
  }

  return pushURL;
};

export const getOutOfOfficeRescheduleTime = (start) => {
  const time = moment(start, "h:mm A").format("HH:mm");
  return `${time.split(":")[0]}:00:00`;
};
